import React, { useState, useMemo, useEffect } from "react"
import styles from '../styles/BookingModal.module.css';

type LayoutData = {
  rows: number
  cols: number
  seats: boolean[][]
}

type SeatState = "available" | "selected" | "booked" | "held"

interface BookingInterfaceProps {
  layout: LayoutData | null
  layoutName: string
  bookedSeats?: string[]
  heldSeats?: string[]
  initialSelectedSeats?: string[]
  onSelectedSeatsChange?: (seats: string[]) => void
  seatNumberingStart?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
}

const SeatIcon = ({ size, status }: { size: number, status: SeatState }) => {
  // green-500, blue-500, gray-400, yellow-500
  const color = status === "available" ? "#22c55e" : status === "selected" ? "#3b82f6" : status === "held" ? "#eab308" : "#9ca3af"

  // Render simple box for tiny sizes (performance/clarity)
  if (size < 12) {
    return (
      <div
        style={{ width: size, height: size, backgroundColor: color }}
        className={`rounded-[1px] ${status === 'booked' || status === 'held' ? 'opacity-50' : ''}`}
      />
    )
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
    >
      {/* Backrest - Curved (Bottom) */}
      <path d="M4 19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V16H4V19Z" fill={color} />
      {/* Seat Cushion (Top) */}
      <rect x="5" y="3" width="14" height="12" rx="2" fill={color} fillOpacity="0.8" />
      {/* Armrests */}
      <rect x="2" y="5" width="2" height="10" rx="1" fill={color} />
      <rect x="20" y="5" width="2" height="10" rx="1" fill={color} />
    </svg>
  )
}

export default function BookingInterface({ layout, layoutName, bookedSeats = [], heldSeats = [], initialSelectedSeats = [], onSelectedSeatsChange, seatNumberingStart = 'top-left' }: BookingInterfaceProps) {
  const [seatStates, setSeatStates] = useState<Map<string, SeatState>>(new Map())
  const [selectedSeats, setSelectedSeats] = useState<string[]>([])
  const [seatSize, setSeatSize] = useState(24)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const handleFit = () => {
    if (!layout || !containerRef.current) return

    const container = containerRef.current

    // Width: Use clientWidth. Subtract minimal padding/borders (approx 16px safe zone)
    // On mobile p-0 means full width, but we might have borders/scrollbars.
    const availableWidth = container.clientWidth - 16

    // Height: constrained by max-h-[60vh] and window height.
    // We calculate the theoretical max available height to avoid "current height" issues.
    const maxPossHeight = window.innerHeight * 0.60
    const availableHeight = maxPossHeight - 40 // Header/Margins overhead

    if (availableWidth <= 0 || availableHeight <= 0) return

    // Precise Gap Formula:
    // Width = seatSize * (cols + 1) + 8 * cols + 8
    // Height = seatSize * (rows + 1) + 8 * rows

    const sizeByWidth = (availableWidth - 8 * layout.cols - 8) / (layout.cols + 1)
    const sizeByHeight = (availableHeight - 8 * layout.rows) / (layout.rows + 1)

    // Allow much smaller seats on mobile to ensure "Fit" constraint is met
    // Min 2px (tiny dots), Max 60px
    const newSize = Math.floor(Math.min(sizeByWidth, sizeByHeight))
    setSeatSize(Math.max(2, Math.min(60, newSize)))
  }

  // Auto-fit on layout change
  useEffect(() => {
    if (layout) {
      // Small timeout to allow modal animation/rendering to settle
      const timer = setTimeout(handleFit, 100)
      return () => clearTimeout(timer)
    }
  }, [layout])

  const rowLabelFromIndex = (idx: number): string => {
    let effectiveIdx = idx;
    if (seatNumberingStart === 'bottom-left' || seatNumberingStart === 'bottom-right') {
      if (layout) {
        effectiveIdx = layout.rows - 1 - idx;
      }
    }
    let n = effectiveIdx
    let s = ''
    while (n >= 0) {
      s = String.fromCharCode(65 + (n % 26)) + s
      n = Math.floor(n / 26) - 1
    }
    return s
  }

  // Track if we've initialized with initialSelectedSeats
  const initializedRef = React.useRef(false)

  useEffect(() => {
    const newStates = new Map<string, SeatState>()

    // Check if we should apply initialSelectedSeats (only once when they change to non-empty)
    const shouldApplyInitial = initialSelectedSeats.length > 0 && !initializedRef.current

    // Mark booked seats
    for (const seat of bookedSeats) {
      newStates.set(seat, "booked")
    }

    // Mark held seats
    for (const seat of heldSeats) {
      // If held seat is also "booked" (shouldn't happen with correct logic but safe to overwrite), held takes visual precedence as "unavailable"
      // But if it's held by THIS user (initialSelectedSeats), we want it selected.
      // However, 'heldSeats' from parent should ideally exclude user's own held seats.
      newStates.set(seat, "held")
    }

    if (shouldApplyInitial) {
      // Mark initial selected seats as selected (overriding held/booked status if present, trusting parent logic)
      for (const seat of initialSelectedSeats) {
        newStates.set(seat, "selected")
      }
      setSeatStates(newStates)
      setSelectedSeats(initialSelectedSeats.slice())
      if (onSelectedSeatsChange) onSelectedSeatsChange(initialSelectedSeats.slice())
      initializedRef.current = true
    } else if (!initializedRef.current || initialSelectedSeats.length === 0) {
      // Normal initialization (no pending seats)
      setSeatStates(newStates)
      if (!initializedRef.current) {
        setSelectedSeats([])
        if (onSelectedSeatsChange) onSelectedSeatsChange([])
        initializedRef.current = true
      }
    } else {
      // Update states without resetting selection if already initialized
      const currentSelected = selectedSeats;
      for (const seat of currentSelected) {
        newStates.set(seat, "selected")
      }
      setSeatStates(newStates)
    }
  }, [bookedSeats, heldSeats, JSON.stringify(initialSelectedSeats), layout?.rows, layout?.cols]) // Fixed: stable array comparison

  // Reset initialization flag when layout changes (different screening selected)
  useEffect(() => {
    initializedRef.current = false
  }, [layout?.rows, layout?.cols])

  const availableSeats = useMemo(() => {
    if (!layout) return 0
    let count = 0
    for (let i = 0; i < layout.rows; i++) {
      for (let j = 0; j < layout.cols; j++) {
        if (layout.seats[i][j]) count++
      }
    }
    return count
  }, [layout])

  const getSeatId = (row: number, col: number): string => {
    const rowLabel = rowLabelFromIndex(row)
    let seatNumber = 0

    const isRightToLeft = seatNumberingStart === 'top-right' || seatNumberingStart === 'bottom-right';

    if (isRightToLeft) {
      if (layout) {
        for (let i = layout.cols - 1; i > col; i--) {
          if (layout.seats[row][i]) seatNumber++
        }
      }
    } else {
      if (layout) {
        for (let i = 0; i < col; i++) {
          if (layout.seats[row][i]) seatNumber++
        }
      }
    }
    return `${rowLabel}${seatNumber + 1}`
  }

  const handleSeatClick = (row: number, col: number) => {
    if (!layout || !layout.seats[row][col]) return

    const seatId = getSeatId(row, col)
    const currentState = seatStates.get(seatId) || "available"

    if (currentState === "booked" || currentState === "held") return

    const newStates = new Map(seatStates)
    const newSelected = selectedSeats.filter((s) => s !== seatId)

    if (currentState === "available") {
      newStates.set(seatId, "selected")
      newSelected.push(seatId)
    } else if (currentState === "selected") {
      newStates.delete(seatId)
    }

    setSeatStates(newStates)
    setSelectedSeats(newSelected.sort())
    if (onSelectedSeatsChange) onSelectedSeatsChange(newSelected.sort())
  }

  if (!layout) {
    return (
      <div className="p-6 bg-gray-700 rounded-lg">
        <p className="text-gray-300">No layout selected.</p>
      </div>
    )
  }

  const bookedCount = Array.from(seatStates.values()).filter((s) => s === "booked" || s === "held").length

  const changeZoom = (delta: number) => {
    setSeatSize(prev => Math.max(10, Math.min(60, prev + delta)))
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-3">
        <div className="p-0 sm:p-2 md:p-8 bg-gray-800 text-white rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-bold mb-2 text-white">{layoutName}</h2>
              <p className="text-gray-300">
                {availableSeats} total seats | {bookedCount} unavailable | {selectedSeats.length} selected
              </p>
            </div>
            {/* Zoom Controls */}
            <div className="flex items-center gap-2 bg-gray-700 p-2 rounded">
              <button onClick={() => changeZoom(-4)} className="p-1 hover:bg-gray-600 rounded" title="Zoom Out">➖</button>
              <span className="text-xs w-8 text-center">{seatSize}px</span>
              <button onClick={() => changeZoom(4)} className="p-1 hover:bg-gray-600 rounded" title="Zoom In">➕</button>
              <button onClick={handleFit} className="ml-2 px-2 py-1 text-xs bg-gray-600 hover:bg-gray-500 rounded" title="Fit Entire Layout">Fit</button>
            </div>
          </div>


          <div className="mb-8 p-6 bg-gray-700 rounded-lg text-center">
            <p className="text-sm text-gray-300 mb-2">SCREEN</p>
            <div className="h-1 bg-gray-500 rounded"></div>
          </div>

          <div
            ref={containerRef}
            className={`flex justify-start mb-8 overflow-auto max-h-[60vh] p-2 border border-gray-700 rounded relative ${styles.hideScrollbar}`}
          >
            <div className="inline-flex flex-col gap-2 min-w-max p-0 md:p-2 mx-auto">
              <div className="flex items-center gap-4">
                <div style={{ width: seatSize }} /> {/* Spacer for row labels */}
                <div className="flex gap-2">
                  {Array.from({ length: layout.cols }).map((_, idx) => {
                    const isRightToLeft = seatNumberingStart === 'top-right' || seatNumberingStart === 'bottom-right';
                    return (
                      <div key={`col-${idx}`}
                        className="text-center text-xs font-semibold text-white flex items-center justify-center"
                        style={{ width: seatSize }}
                      >
                        {isRightToLeft ? layout.cols - idx : idx + 1}
                      </div>
                    );
                  })}
                </div>
              </div>
              {layout.seats.map((row, rowIdx) => (
                <div key={rowIdx} className="flex items-center gap-4">
                  <div
                    className="text-center text-sm font-semibold text-white flex items-center justify-center"
                    style={{ width: seatSize }}
                  >
                    {rowLabelFromIndex(rowIdx)}
                  </div>
                  <div className="flex gap-2">
                    {row.map((hasSeat, colIdx) => {
                      if (!hasSeat) {
                        return <div key={`${rowIdx}-${colIdx}`} style={{ width: seatSize, height: seatSize }} />
                      }
                      const seatId = getSeatId(rowIdx, colIdx)
                      const state = seatStates.get(seatId) || "available"

                      let displaySeatNumber = 0
                      const isRightToLeft = seatNumberingStart === 'top-right' || seatNumberingStart === 'bottom-right';

                      if (isRightToLeft) {
                        for (let i = layout.cols - 1; i > colIdx; i--) {
                          if (layout.seats[rowIdx][i]) displaySeatNumber++
                        }
                      } else {
                        for (let i = 0; i < colIdx; i++) {
                          if (layout.seats[rowIdx][i]) displaySeatNumber++
                        }
                      }

                      return (
                        <button
                          key={seatId}
                          onClick={() => handleSeatClick(rowIdx, colIdx)}
                          disabled={state === "booked" || state === "held"}
                          title={seatId}
                          className={`group transition-transform active:scale-95 flex items-center justify-center ${state === "booked" || state === "held" ? "cursor-not-allowed opacity-80" : "cursor-pointer hover:opacity-90"
                            }`}
                          style={{ width: seatSize, height: seatSize, padding: 0, background: 'transparent', border: 'none' }}
                        >
                          <div className="relative">
                            <SeatIcon size={seatSize} status={state} />
                            {seatSize >= 20 && state !== "booked" && state !== "held" && (
                              <span
                                className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-white pointer-events-none drop-shadow-md"
                                style={{ fontSize: Math.max(8, seatSize * 0.4) }}
                              >
                                {displaySeatNumber + 1}
                              </span>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <div className="flex justify-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-500"></div>
                <span className="text-sm text-white">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-500"></div>
                <span className="text-sm text-white">Selected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-yellow-500"></div>
                <span className="text-sm text-white">Held</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gray-400"></div>
                <span className="text-sm text-white">Booked</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-1">
        <div className="p-6 sticky top-6 bg-gray-800 text-white rounded-lg">
          <h3 className="text-lg font-semibold mb-4 text-white">Selected Seats</h3>
          {selectedSeats.length === 0 ? (
            <p className="text-gray-300 text-sm mb-4">No seats selected</p>
          ) : (
            <>
              <div className="mb-4 p-3 bg-gray-700 rounded-lg">
                <div className="flex flex-wrap gap-2">
                  {selectedSeats.map((seat) => (
                    <div key={seat} className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">
                      {seat}
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-sm text-gray-300">
                Total: {selectedSeats.length} seat(s)
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
